import React, { useState } from 'react';
import axios from 'axios';
import { Mail, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const EmailSignupSection = () => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('idle'); // 'idle' | 'success' | 'error'
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/contacts/subscribe`,
        {
          first_name: firstName,
          email: email
        },
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: 10000
        }
      );
      
      if (response.status === 201 && response.data.success) {
        setSubmitStatus('success');
        setMessage('Thank you for subscribing! Check your email for a welcome message.');
        setEmail('');
        setFirstName('');
      }
      
    } catch (error) {
      setSubmitStatus('error');
      if (axios.isAxiosError(error)) {
        const detail = error.response?.data?.detail;
        if (typeof detail === 'string') {
          setMessage(detail);
        } else if (Array.isArray(detail)) {
          setMessage(detail.map((d) => d?.msg).filter(Boolean).join(' ') || 'Something went wrong. Please try again.');
        } else if (detail && typeof detail === 'object') {
          setMessage(detail.message || 'Something went wrong. Please try again.');
        } else {
          setMessage('Something went wrong. Please try again.');
        }
      } else {
        setMessage('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="email-signup" className="py-20 bg-gradient-to-b from-[#f7f5f2] to-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 border border-[#e8e6e1]">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#a8b5a0]/20 rounded-full mb-6">
              <Mail className="h-8 w-8 text-[#a8b5a0]" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e1919] mb-4">
              Stay Connected
            </h2>
            <p className="text-lg text-[#736c64] max-w-2xl mx-auto">
              Get exclusive sourdough tips, new recipes, and be the first to know about new products from Hearthlowe.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="max-w-lg mx-auto">
            <div className="space-y-4 mb-6">
              <div>
                <Input
                  type="text"
                  placeholder="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 border-[#e8e6e1] focus:border-[#a8b5a0] focus:ring-[#a8b5a0]"
                />
              </div>
              <div>
                <Input
                  type="email"
                  placeholder="Email Address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 border-[#e8e6e1] focus:border-[#a8b5a0] focus:ring-[#a8b5a0]"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#a8b5a0] hover:bg-[#8fa087] text-white py-6 text-lg transition-all"
            >
              {isSubmitting ? 'Subscribing...' : 'Join Our Community'}
            </Button>
          </form>

          {submitStatus === 'success' && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3 animate-in fade-in slide-in-from-top duration-300">
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-green-800 text-sm">{message}</p>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3 animate-in fade-in slide-in-from-top duration-300">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-red-800 text-sm">{message}</p>
            </div>
          )}

          <p className="text-center text-xs text-[#736c64] mt-6">
            We respect your privacy. Unsubscribe anytime.
          </p>
        </div>
      </div>
    </section>
  );
};

export default EmailSignupSection;
